import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TAlertMessages, useAlerts } from "context/AlertContext";
import { IStructuredTemplate } from "models/structuredTemplates.models";
import { services } from "services";
import { AlertSeverity } from "utils/constants/alerts.constants";
import { QueryAPIKey } from "utils/constants/api.constants";

type TUpdateStructuredTemplateMutationData = {
  templateId: string;
  projectId: string;
  alertMessages?: Partial<TAlertMessages>;
  data: IStructuredTemplate;
};

const useUpdateStructuredTemplate = (
  alertMessages?: Partial<TAlertMessages>,
) => {
  const { showAlert } = useAlerts();
  const queryClient = useQueryClient();

  const { data, isError, isPending, isSuccess, mutate, mutateAsync } =
    useMutation({
      mutationFn: ({
        templateId,
        projectId,
        data,
      }: TUpdateStructuredTemplateMutationData) =>
        services.structuredTemplates
          .updateStructuredTemplate(templateId, projectId, data)
          .then((res) => {
            alertMessages?.successMessage &&
              showAlert({
                severity: AlertSeverity.Success,
                message: alertMessages?.successMessage,
              });

            queryClient.invalidateQueries({
              queryKey: [QueryAPIKey.GetStructuredTemplates],
            });
            queryClient.invalidateQueries({
              queryKey: [QueryAPIKey.GetStructuredTemplate],
            });

            return res.data as IStructuredTemplate;
          })
          .catch((error) => {
            alertMessages?.failureMessage &&
              showAlert({
                severity: AlertSeverity.Error,
                message: alertMessages?.failureMessage,
              });

            throw error;
          }),
    });

  return {
    data,
    isPending,
    isError,
    isSuccess,
    updateStructuredTemplate: mutate,
    updateStructuredTemplateAsync: mutateAsync,
  };
};

export default useUpdateStructuredTemplate;
